import styled, { css } from 'styled-components';
import { v2Colors, v2FontFamily, v2Breakpoints } from '../../../../formInputsV2/styles/tokens';
import {
  AiChatButton,
  CompanyInfoElements,
  CompanyInformationContainer,
  LogoContainer,
  LogoPriceContainer,
  Price,
  PriceContainer,
  QuoteInformationContainer,
  QuoteInformationItem,
} from './QuotationCard.styles';

// QuoteInfoComponentResult renders a Fragment, so every group it returns
// (the info-links row and each details grid) lands as a direct flex child of
// LeftCol below, not nested inside a wrapper. Some companies (e.g. Adriatic,
// company_id 1/3/9/10/17/21/22) reuse QuoteInformationContainer — margin-top
// 12px, meant for stacking under normal block flow inside the modal's RightCol
// — for that first info-links row too, which double-counts against LeftCol's
// own flex `gap`. Zeroing it here keeps every group's spacing coming from one
// place (LeftCol's gap) instead of two spacings adding together.

// v2 look for the read-only "already selected" cards shown inside the v2
// guarantee-slot cards (ResultSlotCard.styles) once a quote has been picked:
// QuotationCardResult (the selected main quote) and RiskCardSelected (a
// selected risk quote). Neither has a Seleziona button of its own — that
// lives in the slot's own Aggiungi/Rimuovi row — so these are simpler than
// the selectable QuotationCard/RiskCard: one column, no button.
//
// Exports match the two old style files' names 1:1 so QuotationCardResult.jsx
// and RiskCardSelected.jsx only need an import-path change, no markup
// changes. The old files (common/reusable/QuotationCardResult.styles.js,
// Cards/RiskCardSelected.styles.js) stay untouched — Luce.jsx still uses
// them.
export {
  AiChatButton,
  CompanyInfoElements,
  CompanyInformationContainer,
  LogoPriceContainer,
  PriceContainer,
  Price as PriceResult,
  LogoContainer as LogoResultContainer,
  QuoteInformationContainer as QuoteInformationResultContainer,
  QuoteInformationItem as QuoteInformationResultItem,
};

// Single-column layout: warning -> logo/price/AI row -> info links -> details
export const LeftCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

// RiskCardSelected wraps everything in an extra `.row-container` div; make it
// a transparent pass-through rather than restyling LeftCol's own parent.

const cardBase = css`
  position: relative;
  width: 100%;
  padding: 16px 20px;
  background: ${v2Colors.inputBackground};
  border: 1px solid ${v2Colors.disabledBorder};
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(16, 24, 40, 0.05);
  font-family: ${v2FontFamily};

  .row-container {
    display: contents;
  }

  &.selected {
    border-color: ${v2Colors.primary};
    box-shadow: 0 0 0 3px ${v2Colors.primarySoftHover};
  }

  ${AiChatButton} {
    position: absolute;
    top: 16px;
    right: 20px;
  }

  /* This card is a read-only summary (no Seleziona button, no quality badge
     taking up the top row), so its own logo/price can run a notch smaller
     than the modal's selectable QuotationCard and still read fine — keeps
     the whole card visually lighter/shorter on the result page. */
  ${LogoContainer} {
    width: 97px;
    height: 57px;
  }

  ${Price} h2 {
    font-size: 24px;
  }

  /* Hairline under the logo/price row, same as the modal's QuotationCard
     RightCol divider — everything below (info links, details grid) reads as
     one group under the price, matching the modal card's look. Tighter than
     the modal's own gap since this card has no button row pulling focus
     downward, so a shorter gap above "Maggiori informazioni" still reads
     clearly separated. */
  ${LogoPriceContainer} {
    padding-bottom: 10px;
    padding-right: 120px;
    border-bottom: 1px solid ${v2Colors.disabledSurface};
  }

  ${QuoteInformationContainer} {
    margin-top: 0;
  }

  /* Company quality index badge (CompanyQualityComponent) — same v2 look as
     the modal's QuotationCard, which styles this Bootstrap .badge the same
     way in QuotationCard.styles.js */
  .badge {
    border-radius: 999px;
    font-family: ${v2FontFamily};
    font-weight: 600;
    letter-spacing: 0;
  }

  .badge .fs-6 {
    font-size: 13px !important;
  }

  /* Semestrale-downgrade warning (Linear / Genial Più) — right padding keeps
     its text clear of the AI button pinned to the card's corner above it */
  .alert.alert-warning {
    margin: 0 !important;
    padding: 8px 130px 8px 12px !important;
    border: 1px solid ${v2Colors.warningBorder};
    border-radius: 10px;
    background: ${v2Colors.warningSoft};
    color: ${v2Colors.warningText};
    font-family: ${v2FontFamily};
    font-size: 12.5px;
    line-height: 1.4;
  }

  /* "Servizio non assicurativo" (My Rescue) */
  .alert.alert-danger {
    width: fit-content;
    margin: 0 !important;
    padding: 8px 12px !important;
    border: 1px solid ${v2Colors.dangerBorder};
    border-radius: 10px;
    background: ${v2Colors.dangerSoft};
    color: ${v2Colors.danger};
    font-family: ${v2FontFamily};
    font-size: 12.5px;
    font-weight: 600;
    line-height: 1.4;
  }

  /* This card sits inside a 2-up grid of slot cards, so its own width shrinks
     well before the viewport reaches the shared AiChatButton's own <480px
     icon-only breakpoint (that one only helps a genuine phone-width viewport,
     not a narrow column on a wider screen). A laptop-width screen is already
     narrow enough for that column, so collapse to icon-only starting at the
     "desktop" breakpoint here rather than waiting for "tablet" — the label
     stays for screen readers, same technique the button already uses for
     its own narrow-viewport case. */
  @media (max-width: ${v2Breakpoints.desktop}) {
    ${AiChatButton} {
      padding: 8px;

      span {
        position: absolute;
        width: 1px;
        height: 1px;
        overflow: hidden;
        clip: rect(0 0 0 0);
        white-space: nowrap;
      }
    }

    /* Icon-only button needs far less clearance than the full-text one */
    ${LogoPriceContainer} {
      padding-right: 48px;
    }
  }

  @media (max-width: ${v2Breakpoints.tablet}) {
    padding: 14px 16px;

    ${AiChatButton} {
      top: 14px;
      right: 16px;
    }

    /* Everything but the price itself reads smaller on mobile, so the
       amount stays the one thing that pops in this cramped, one-column
       layout. */
    ${Price} h5,
    ${Price} h6,
    ${Price} p {
      font-size: 11px;
    }

    ${CompanyInfoElements} p {
      font-size: 11.5px;
    }

    ${QuoteInformationItem} h6 {
      font-size: 10.5px;
    }

    ${QuoteInformationItem} p {
      font-size: 12.5px;
    }
  }
`;

export const QuotationCardResultStyles = styled.div`${cardBase}`;

// Same card language as QuotationCardResultStyles — kept as a separate export
// (rather than one shared name) only so RiskCardSelected.jsx's import
// doesn't need renaming.
//
// min-height is a flat approximation, not a real fix: a lighter guarantee
// (Tutela Legale, Infortuni Conducente) has much less to show than Res
// Civile, so its card reads noticeably shorter next to it. Actually
// stretching it to match its slot's height needs RiskCard's own flex
// layout to cooperate (see ResultSlotCard.styles.js), which is out of
// scope here — this just closes most of the gap with a fixed value.
export const RiskCardSelectedStyles = styled.div`${cardBase}
  min-height: 200px;

  @media (max-width: ${v2Breakpoints.tablet}) {
    min-height: 0;
  }
`;

// Positioning now happens on the AI button itself (see cardBase above), so
// this wrapper is just a pass-through — kept only so RiskCardSelected.jsx's
// markup doesn't need to change.
export const ButtonChat = styled.div`
  display: contents;
`;
